import { Fragment, useMemo, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import date from "date-and-time";
import clsx from "clsx";

import useTickets from "../../../data/Ticket/useTickets";
import useCustomers from "../../../data/Customer/useCustomers";
import useAuth from "../../../hooks/useAuth";
import { canonicalStaffEmail, isTicketAssignedToUser } from "../../../constants/ticketAssignees";
import LoadingWheel from "../LoadingWheel";
import { errorResolver } from "../../../utils/errorResolver";
import { ITicket, ITicketNote } from "../../../models/Tickets/Ticket";
import {
  countPublicNoteReplies,
  isAwaitingStaffPublicReply,
  isPublicStaffNote,
} from "../../../utils/ticketNoteReplies";

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 45_000) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"}`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"}`;
  return date.format(new Date(iso), "DD/MM/YYYY");
}

/**
 * Newest instant in the *conversation* (ticket `created` + note `created` only).
 * Excludes `ticket.updated` so ordering matches the preview line and date chips.
 */
function ticketThreadLastActivity(t: ITicket): { at: string; ms: number } {
  let at = t.created;
  let ms = new Date(t.created).getTime();
  for (const n of t.notes ?? []) {
    const nMs = new Date(n.created).getTime();
    if (nMs > ms) {
      ms = nMs;
      at = n.created;
    }
  }
  return { at, ms };
}

function latestActivityPreview(t: ITicket): {
  at: string;
  authorLabel: string;
  snippet: string;
  isInternal: boolean;
} {
  type C = { at: string; authorLabel: string; text: string; isInternal: boolean };
  const items: C[] = [
    {
      at: t.created,
      authorLabel: "New Ticket",
      text: t.description || "(No description)",
      isInternal: false,
    },
  ];
  for (const n of t.notes ?? []) {
    items.push({
      at: n.created,
      authorLabel: n.authorName ?? n.authorEmail ?? (n.isInternal ? "Staff (internal)" : "Reply"),
      text: n.note,
      isInternal: n.isInternal,
    });
  }
  items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  const top = items[0];
  const snippet =
    top.text.length > 160 ? `${top.text.slice(0, 157).trimEnd()}…` : top.text;
  return { at: top.at, authorLabel: top.authorLabel, snippet, isInternal: top.isInternal };
}

function calendarDayLabel(iso: string): string {
  return date.format(new Date(iso), "dddd, MMMM D");
}

function publicThreadNotes(t: ITicket): ITicketNote[] {
  return [...(t.notes ?? [])]
    .filter((n) => !n.isInternal)
    .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
}

/** Same customer id → same colours (avatar + tag). */
const CUSTOMER_THREAD_PALETTE: { avatar: string; tag: string }[] = [
  { avatar: "bg-rose-600 text-white", tag: "bg-rose-500/20 text-rose-100 border-rose-500/45" },
  { avatar: "bg-orange-600 text-white", tag: "bg-orange-500/20 text-orange-100 border-orange-500/45" },
  { avatar: "bg-amber-600 text-white", tag: "bg-amber-500/20 text-amber-100 border-amber-500/45" },
  { avatar: "bg-lime-600 text-white", tag: "bg-lime-500/20 text-lime-100 border-lime-500/45" },
  { avatar: "bg-emerald-600 text-white", tag: "bg-emerald-500/20 text-emerald-100 border-emerald-500/45" },
  { avatar: "bg-teal-600 text-white", tag: "bg-teal-500/20 text-teal-100 border-teal-500/45" },
  { avatar: "bg-cyan-600 text-white", tag: "bg-cyan-500/20 text-cyan-100 border-cyan-500/45" },
  { avatar: "bg-sky-600 text-white", tag: "bg-sky-500/20 text-sky-100 border-sky-500/45" },
  { avatar: "bg-indigo-600 text-white", tag: "bg-indigo-500/20 text-indigo-100 border-indigo-500/45" },
  { avatar: "bg-violet-600 text-white", tag: "bg-violet-500/20 text-violet-100 border-violet-500/45" },
];

function customerColorIndex(customerId: string): number {
  let h = 2166136261;
  for (let i = 0; i < customerId.length; i++) {
    h ^= customerId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % CUSTOMER_THREAD_PALETTE.length;
}

export interface TicketActivityDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function ticketAttentionBadgeCount(
  tickets: ITicket[] | undefined,
  userEmail: string | null | undefined,
): number {
  if (!tickets?.length) return 0;
  return tickets.filter(
    (t) => isAwaitingStaffPublicReply(t) && isTicketAssignedToUser(t, userEmail),
  ).length;
}

export default function TicketActivityDrawer({ open, onClose }: TicketActivityDrawerProps) {
  // The ticket list is paginated server-side; the drawer needs the notes to work
  // out what's awaiting a reply, and a page large enough to cover active work.
  const { tickets } = useTickets({ itemsPerPage: 200, includeNotes: true });
  const { customers } = useCustomers();
  const { user } = useAuth();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const myStaffEmail = canonicalStaffEmail(user?.email ?? undefined);

  const customerName = (customerId: string) =>
    customers.data?.find((c) => c.id === customerId)?.name ?? customerId;

  const sortedTickets = useMemo(() => {
    const list = (tickets.data?.data ?? []).filter(
      (t) => isAwaitingStaffPublicReply(t) && isTicketAssignedToUser(t, user?.email),
    );
    return [...list]
      .sort(
        (a, b) => ticketThreadLastActivity(b).ms - ticketThreadLastActivity(a).ms,
      )
      .slice(0, 50);
  }, [tickets.data, user?.email]);

  const rowsWithDay = useMemo(() => {
    const out: {
      ticket: ITicket;
      preview: ReturnType<typeof latestActivityPreview>;
      threadLastAt: string;
      showDay: boolean;
      dayLabel: string;
    }[] = [];
    let prevDay = "";
    for (const ticket of sortedTickets) {
      const preview = latestActivityPreview(ticket);
      const { at: threadLastAt } = ticketThreadLastActivity(ticket);
      const day = date.format(new Date(threadLastAt), "YYYY-MM-DD");
      const showDay = day !== prevDay;
      prevDay = day;
      out.push({
        ticket,
        preview,
        threadLastAt,
        showDay,
        dayLabel: calendarDayLabel(threadLastAt),
      });
    }
    return out;
  }, [sortedTickets]);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[80]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[1px]" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 z-[81] flex justify-end pointer-events-none">
          <TransitionChild
            as={Fragment}
            enter="transform transition ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="pointer-events-auto flex h-full w-full max-w-md flex-col bg-slate-900 text-slate-100 shadow-2xl border-l border-slate-700">
              <div className="flex items-center justify-between gap-3 border-b border-slate-700 px-4 py-3 shrink-0">
                <div>
                  <DialogTitle className="text-lg font-semibold text-white">
                    Ticket activity
                  </DialogTitle>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Public customer replies assigned to you, waiting for your reply (newest first).
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-full p-2 text-slate-400 hover:bg-slate-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                {tickets.isLoading || customers.isLoading ? (
                  <div className="flex justify-center py-16">
                    <LoadingWheel />
                  </div>
                ) : tickets.isError ? (
                  <p className="px-4 py-6 text-sm text-red-300">
                    {errorResolver(tickets.error)}
                  </p>
                ) : rowsWithDay.length === 0 ? (
                  <p className="px-4 py-10 text-sm text-slate-500 text-center">
                    {!myStaffEmail
                      ? "Your signed-in user is not on the ticket assignee list, so nothing is shown here."
                      : "No customer replies waiting for you on tickets assigned to you."}
                  </p>
                ) : (
                  <ul className="pb-6">
                    {rowsWithDay.map(({ ticket, preview, threadLastAt, showDay, dayLabel }) => {
                      const { staff: staffReplies, customer: customerReplies } =
                        countPublicNoteReplies(ticket);
                      const publicTotal = staffReplies + customerReplies;
                      const expanded = expandedId === ticket.id;
                      const thread = publicThreadNotes(ticket);
                      const cust = customerName(ticket.customerId);
                      const initials = cust
                        .split(/\s+/)
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase() || "?";
                      const cv = CUSTOMER_THREAD_PALETTE[customerColorIndex(ticket.customerId)];

                      return (
                        <li key={ticket.id}>
                          {showDay ? (
                            <div className="sticky top-0 z-10 px-4 py-2 bg-slate-900/95 border-b border-slate-800">
                              <span className="inline-block rounded-full bg-slate-800 px-3 py-1 text-[11px] font-medium text-slate-300">
                                {dayLabel}
                              </span>
                            </div>
                          ) : null}
                          <div className="border-l-[3px] border-pink-500">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedId((id) => (id === ticket.id ? null : ticket.id))
                              }
                              className="w-full text-left px-4 py-3 hover:bg-slate-800/80 transition-colors"
                            >
                              <div className="flex gap-3">
                                <div className="relative shrink-0">
                                  <div
                                    className={clsx(
                                      "flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold shadow-inner",
                                      cv.avatar,
                                    )}
                                  >
                                    {initials}
                                  </div>
                                  <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-slate-900 ring-2 ring-slate-900">
                                    <ChatBubbleLeftRightIcon className="h-3 w-3 text-orange-400" />
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-semibold text-white truncate min-w-0">
                                      {preview.authorLabel}
                                    </p>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {publicTotal > 0 ? (
                                        <span
                                          className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-pink-600 px-1 text-[10px] font-bold text-white"
                                          title="Public replies in thread"
                                        >
                                          {publicTotal}
                                        </span>
                                      ) : null}
                                      <span
                                        className="text-[11px] text-slate-500 whitespace-nowrap"
                                        title={date.format(
                                          new Date(threadLastAt),
                                          "DD/MM/YYYY HH:mm",
                                        )}
                                      >
                                        {formatRelativeTime(threadLastAt)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                    <span
                                      className={clsx(
                                        "inline-flex max-w-full items-center gap-1.5 truncate rounded-md border px-2 py-0.5",
                                        cv.tag,
                                      )}
                                      title={`Customer: ${cust}`}
                                    >
                                      <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide opacity-80">
                                        Customer
                                      </span>
                                      <span className="min-w-0 truncate text-[11px] font-medium normal-case">
                                        {cust}
                                      </span>
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">
                                    <span className="text-slate-500">Thread in ticket: </span>
                                    <span className="text-slate-300">{ticket.subject}</span>
                                  </p>
                                  <p
                                    className={`text-xs mt-1 line-clamp-2 ${
                                      preview.isInternal ? "text-purple-300 italic" : "text-slate-300"
                                    }`}
                                  >
                                    {preview.snippet}
                                  </p>
                                  <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-500">
                                    {expanded ? (
                                      <ChevronDownIcon className="h-3.5 w-3.5" />
                                    ) : (
                                      <ChevronRightIcon className="h-3.5 w-3.5" />
                                    )}
                                    {expanded ? "Hide thread" : "Show public thread"}
                                  </div>
                                </div>
                              </div>
                            </button>

                            {expanded ? (
                              <div className="px-4 pb-4 pl-[3.25rem] space-y-2 border-b border-slate-800/80">
                                <div className="rounded-lg bg-slate-800/60 border border-slate-700/80 px-3 py-2 text-xs">
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                    New Ticket · {cust}
                                  </p>
                                  <p className="text-slate-200 whitespace-pre-wrap">
                                    {ticket.description || "—"}
                                  </p>
                                </div>
                                {thread.length === 0 ? (
                                  <p className="text-xs text-slate-500">No public replies yet.</p>
                                ) : (
                                  thread.map((note) => {
                                    const staff = isPublicStaffNote(note);
                                    const who =
                                      note.authorName ??
                                      note.authorEmail ??
                                      (staff ? "Staff" : "Customer");
                                    return (
                                      <div
                                        key={note.id}
                                        className={`rounded-lg border px-3 py-2 text-xs ${
                                          staff
                                            ? "bg-orange-950/40 border-orange-800/50 ml-4"
                                            : "bg-slate-800/40 border-slate-700/80 mr-4"
                                        }`}
                                      >
                                        <p className="text-[10px] text-slate-500 mb-0.5">
                                          {who} ·{" "}
                                          {date.format(new Date(note.created), "DD/MM/YYYY HH:mm")}
                                        </p>
                                        <p className="text-slate-200 whitespace-pre-wrap">
                                          {note.note}
                                        </p>
                                      </div>
                                    );
                                  })
                                )}
                                <Link
                                  to={`/tickets/${ticket.id}`}
                                  onClick={onClose}
                                  className="inline-block text-xs font-medium text-orange-400 hover:text-orange-300 pt-1"
                                >
                                  Open full ticket →
                                </Link>
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
